import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { matchPath, Outlet, ScrollRestoration, useLocation, useMatch } from 'react-router-dom';

import useAccountQuery from 'api/queries/useAccountQuery';
import useReferralCreditsQuery from 'api/queries/useReferralCreditsQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { SignupQuizStepsWithFooter } from 'constants/signup';
import Cart from 'features/shop/cart/Cart';
import CartSeed from 'features/shop/cart/CartSeed';
import CartSheet from 'features/shop/cart/CartSheet';
import DevBar from 'features/site/DevBar';
import Footer from 'features/site/Footer';
import Header from 'features/site/Header';
import ReferralNanoBanner from 'features/site/ReferralNanoBanner';
import SignupNanoBanner from 'features/site/SignupNanoBanner';
import useAnalyticsIdentify from 'hooks/useAnalyticsIdentify';
import useHandleReferralNanoBannerScroll from 'hooks/useHandleReferralNanoBannerScroll';
import useSignupNanoBanner from 'hooks/useSignupNanoBanner';
import useStickyShopNavigation, { STICKY_SHOP_NAVIGATION_STYLES } from 'hooks/useStickyShopNavigation';
import useTailwindTheme from 'hooks/useTailwindTheme';
import useTrailingSlashRedirect from 'hooks/useTrailingSlashRedirect';
import useValidReferralsCampaign from 'hooks/useValidReferralsCampaign';
import { getIsProduction } from 'utils';
import { setUserId, updateProfile } from 'utils/analyticsUtils';
import { getSignupLocalStorage } from 'utils/signupUtils';

function App() {
    const { data: account = {}, isLoading: isAccountLoading } = useAccountQuery();
    const { data: referrals = {} } = useReferralCreditsQuery();
    const { data: subscription = {}, isLoading: isSubscriptionLoading } = useSubscriptionQuery();

    const stickyShopNavigation = useStickyShopNavigation();

    const [showBoostedTermsModal, setShowBoostedTermsModal] = useState(false);
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [showPromoTermsModal, setShowPromoTermsModal] = useState(false);

    const location = useLocation();
    const isAcquisitionPDPRoute = !!useMatch('/:slug/p');
    const isShopRoute = !!useMatch('/shop/*');
    const isLeadCapRoute = useMatch('/join');
    const isSignupRoute = !!useMatch('/join/*');
    const isSignupQuizRoute = !!useMatch('/join/quiz/*');
    const isAssortmentRoute = !!useMatch('/assortment-preview-module-for-mango');
    const isSignupQuizRouteWithoutFooter = isSignupQuizRoute && !SignupQuizStepsWithFooter.includes(location?.pathname);
    const isLoginRoute = !!useMatch('/login*');
    const urlParams = new URLSearchParams(window.location.search);
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const { discountCodeData, renderSignupNanoBanner } = useSignupNanoBanner([appliedPromo]);
    const nativeApp = window.isNativeApp || urlParams.get('app') === 'true';
    const isAnalyticsReady = !isAccountLoading && !isSubscriptionLoading;
    useAnalyticsIdentify({ account, subscription, isAnalyticsReady });
    const isCartSeedRoute =
        isShopRoute ||
        ['/account', '/account/upcoming-deliveries'].find((cartSeedPath) => matchPath(cartSeedPath, location.pathname));
    const isStickyShopNavigationRoute = isShopRoute && !location.pathname.startsWith('/shop/product/');

    const referralNanoBannerVisible = useHandleReferralNanoBannerScroll();
    const validReferralsCampaign = useValidReferralsCampaign();

    useTrailingSlashRedirect();

    window.addEventListener('promoApplied', () => {
        setAppliedPromo(getSignupLocalStorage()?.discountCode);
    });

    useEffect(() => {
        if (account?.customerID) {
            setUserId(account.customerID);
        }
    }, [account?.customerID]);

    useEffect(() => {
        if (account?.brand) {
            updateProfile({ brand: account.brand });
        }
    }, [account?.brand]);

    return (
        <>
            {account?.marketplaceOpen && isCartSeedRoute && <CartSeed />}
            {!isLoginRoute && !isAssortmentRoute && (
                <div
                    className={clsx('z-[1200] w-full', {
                        [`sticky ${STICKY_SHOP_NAVIGATION_STYLES.negativeTop} ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                            stickyShopNavigation.hideSiteNav && mobile && isStickyShopNavigationRoute,
                        [`sticky top-0 ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                            !stickyShopNavigation.hideSiteNav && mobile && isStickyShopNavigationRoute,
                        'fixed top-0':
                            (!mobile || !isStickyShopNavigationRoute) &&
                            !nativeApp &&
                            !isSignupRoute &&
                            !isAcquisitionPDPRoute,
                        'sticky top-0': isAcquisitionPDPRoute,
                    })}
                >
                    {validReferralsCampaign && (
                        <ReferralNanoBanner
                            mobile={mobile}
                            referralNanoBannerVisible={referralNanoBannerVisible}
                            referralRewards={referrals?.referralRewards}
                            setShowBoostedTermsModal={setShowBoostedTermsModal}
                            showBoostedTermsModal={showBoostedTermsModal}
                        />
                    )}
                    {renderSignupNanoBanner && (
                        <SignupNanoBanner
                            mobile={mobile}
                            discountCodeInfo={discountCodeData?.disc}
                            setShowPromoTermsModal={setShowPromoTermsModal}
                            showPromoTermsModal={showPromoTermsModal}
                        />
                    )}
                    {!nativeApp && (
                        <Header
                            subscription={subscription}
                            account={account}
                            isShopRoute={isShopRoute}
                            isAcquisitionPDPRoute={isAcquisitionPDPRoute}
                        />
                    )}
                </div>
            )}
            <Outlet />
            {!nativeApp &&
                !isShopRoute &&
                !isSignupQuizRouteWithoutFooter &&
                !isLeadCapRoute &&
                !isLoginRoute &&
                !isAssortmentRoute && <Footer />}
            {!nativeApp && !getIsProduction() && <DevBar />}
            {!nativeApp && account?.marketplaceOpen && mobile && isShopRoute && <CartSheet mobile orderMinOnly />}
            {account?.marketplaceOpen && isShopRoute && <Cart />}
            <ScrollRestoration />
        </>
    );
}

export default App;
